import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import Breadcrumb from '@/components/Breadcrumb'
import MetaTags from '@/components/MetaTags'
import usePreRegistration from '@/services/http/preRegistration/create'
import { getCartPresetByCode } from '@/services/http/cart-presets/getCartPreset'
import { validateCoupon } from '@/services/http/coupons/validateCoupon'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Loader2, ArrowLeft, Shield, Lock, CreditCard } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'

interface Addon {
  id: string
  product_id: string
  name: string
  description: string
  original_price: number
  price: number
  thumbnail_url?: string
}

interface CheckoutItem {
  productId: string
  productName: string
  productImage: string
  orientation: string
  pages: number
  price: number
  coverType?: string
  selectedSubProduct?: {
    id: string
    horizontal: number
    vertical: number
  }
  selectedPageOption?: {
    id: string
    numberPages: number
    price: number
    maximumPhotos: number
  }
  selectedCuratory?: {
    id: string
    value: number
    number_photos: number
  }
  selectedAddons?: Addon[]
}

function CouponToast({
  message,
  onClose,
}: {
  message: string
  onClose: () => void
}) {
  useEffect(() => {
    const id = setTimeout(onClose, 5000)
    return () => clearTimeout(id)
  }, [onClose])

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="pointer-events-auto fixed left-1/2 -translate-x-1/2 bottom-6 z-[1000] w-[min(560px,92%)]"
    >
      <div className="rounded-lg border border-red-200 bg-red-50 text-red-900 shadow-2xl ring-1 ring-red-100">
        <div className="flex items-start gap-4 p-4 md:p-5">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            className="flex-none mt-0.5"
            aria-hidden="true"
          >
            <path
              fill="currentColor"
              d="M11 7h2v6h-2zm0 8h2v2h-2zM1 21h22L12 2L1 21z"
            />
          </svg>
          <div className="flex-1">
            <p className="text-base md:text-lg leading-snug">{message}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar alerta"
            className="ml-auto inline-flex rounded px-2 py-1 hover:bg-red-100"
          >
            x
          </button>
        </div>
      </div>
    </div>
  )
}

const Checkout = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { toast } = useToast()

  const [isLoading, setIsLoading] = useState(false)
  const [isFetchingCep, setIsFetchingCep] = useState(false)
  const [isLoadingCart, setIsLoadingCart] = useState(false)
  const [cartError, setCartError] = useState<string | null>(null)
  const [checkoutData, setCheckoutData] = useState<CheckoutItem | null>(null)

  // Estados do formulário
  const [formData, setFormData] = useState({
    email: '',
    confirmedEmail: '',
    name: '',
    document: '',
    paymentMethod: 'pix',
    phone: '',
    zipCode: '',
    street: '',
    neighborhood: '',
    city: '',
    state: '',
    number: '',
    complement: '',
    installment: '1',
  })

  // Estados do cupom
  const [originalPrice, setOriginalPrice] = useState<number>(0)
  const [couponCode, setCouponCode] = useState('')
  const [discountValue, setDiscountValue] = useState(0)
  const [appliedCouponId, setAppliedCouponId] = useState<string | null>(null)
  const [newTotal, setNewTotal] = useState<number | null>(null)
  const [couponError, setCouponError] = useState<string | null>(null)
  const [showCouponToast, setShowCouponToast] = useState<boolean>(false)
  const couponInputRef = useRef<HTMLInputElement>(null)

  // Estados de validação
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [termsError, setTermsError] = useState(false)
  const [formError, setFormError] = useState(false)
  const [cepError, setCepError] = useState<string | null>(null)
  const [phoneError, setPhoneError] = useState<string | null>(null)
  const [cpfError, setCpfError] = useState<string | null>(null)
  const [apiErrors, setApiErrors] = useState<
    Array<{ title: string; detail: string }>
  >([])

  const preRegister = usePreRegistration

  const format = (value: number) =>
    new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)

  const normalizeAddons = (addons: any[]): Addon[] => {
    if (!addons || addons.length === 0) return []

    return addons.map((addon: any) => {
      if (addon.name && addon.product_id) {
        return {
          id: addon.id,
          product_id: addon.product_id,
          name: addon.name,
          description: addon.description || '',
          original_price: addon.original_price || 0,
          price: addon.price,
          thumbnail_url: addon.thumbnail_url,
        }
      }
      if (addon.product) {
        return {
          id: addon.product.id,
          product_id: addon.product.id,
          name: addon.product.name,
          description: addon.product.description || '',
          original_price: parseFloat(
            addon.product.original_price || addon.product.price,
          ),
          price: parseFloat(addon.product.price),
          thumbnail_url: addon.product.thumbnail_url,
        }
      }
      return addon
    })
  }

  const fetchCartPreset = useCallback(
    async (code: string) => {
      setIsLoadingCart(true)
      setCartError(null)

      try {
        const response = await getCartPresetByCode(code)
        const cartData = response.data.attributes
        const orderItem = cartData.order_items_attributes[0]

        const mappedData: CheckoutItem = {
          productId: orderItem.product.id,
          productName: orderItem.product.name,
          productImage: orderItem.product.thumbnail_url,
          orientation: `${orderItem.dynamic_product?.height || 0}x${orderItem.dynamic_product?.width || 0} cm`,
          pages: orderItem.dynamic_line?.number_pages || 0,
          price: parseFloat(orderItem.unitary_value),
          selectedSubProduct: orderItem.dynamic_product
            ? {
                id: orderItem.dynamic_product.id,
                horizontal: parseFloat(orderItem.dynamic_product.height),
                vertical: parseFloat(orderItem.dynamic_product.width),
              }
            : undefined,
          selectedPageOption: orderItem.dynamic_line
            ? {
                id: orderItem.dynamic_line.id,
                numberPages: orderItem.dynamic_line.number_pages,
                price: parseFloat(orderItem.dynamic_line.price),
                maximumPhotos: orderItem.dynamic_line.maximum_photos,
              }
            : undefined,
          selectedCuratory: orderItem.curatory
            ? {
                id: orderItem.curatory.id,
                value: parseFloat(orderItem.curatory.price),
                number_photos: orderItem.curatory.number_photos,
              }
            : undefined,
          selectedAddons: normalizeAddons(
            orderItem.sub_order_items_attributes || [],
          ),
        }

        setCheckoutData(mappedData)
        setOriginalPrice(parseFloat(orderItem.unitary_value))

        if (cartData.coupon_id && cartData.coupon_code) {
          setCouponCode(cartData.coupon_code)
          setAppliedCouponId(cartData.coupon_id)
          setDiscountValue(parseFloat(cartData.applied_coupon_discount))
          setNewTotal(parseFloat(cartData.net_value))
        }
      } catch (error) {
        setCartError(
          'Não foi possível carregar os dados do carrinho. Tente novamente.',
        )
        toast({
          title: 'Erro',
          description: 'Não foi possível carregar os dados do carrinho.',
          variant: 'destructive',
        })
      } finally {
        setIsLoadingCart(false)
      }
    },
    [toast],
  )

  useEffect(() => {
    const code = searchParams.get('code')

    if (code) {
      fetchCartPreset(code)
    } else {
      const state = location.state as CheckoutItem | null
      if (state) {
        const normalizedState = {
          ...state,
          selectedAddons: normalizeAddons(state.selectedAddons || []),
        }
        setCheckoutData(normalizedState)
        setOriginalPrice(state.price)
      }
    }
  }, [searchParams, location.state, navigate, fetchCartPreset])

  // Máscaras
  const formatPhone = (phone: string) => {
    const numbers = phone.replace(/\D/g, '')
    const match = numbers.match(/^(\d{0,2})(\d{0,5})(\d{0,4})$/)
    if (!match) return ''
    return !match[2]
      ? match[1]
      : `(${match[1]}) ${match[2]}${match[3] ? `-${match[3]}` : ''}`
  }

  const formatCep = (cep: string) => {
    const numbers = cep.replace(/\D/g, '')
    const match = numbers.match(/^(\d{0,5})(\d{0,3})$/)
    if (!match) return ''
    return !match[2] ? `${match[1]}` : `${match[1]}-${match[2]}`
  }

  const formatCpf = (cpf: string) => {
    const numbers = cpf.replace(/\D/g, '')
    const match = numbers.match(/^(\d{0,3})(\d{0,3})(\d{0,3})(\d{0,2})$/)
    if (!match) return ''
    return !match[2]
      ? match[1]
      : `${match[1]}.${match[2]}${match[3] ? `.${match[3]}` : ''}${
          match[4] ? `-${match[4]}` : ''
        }`
  }

  // Validações
  const validateCpf = (cpf: string) => {
    const cleanedCpf = cpf.replace(/\D/g, '')
    if (cleanedCpf.length !== 11) return 'CPF inválido (deve ter 11 dígitos)'
    if (/^(\d)\1{10}$/.test(cleanedCpf)) return 'CPF inválido'
    let sum = 0
    let remainder

    for (let i = 1; i <= 9; i++) {
      sum += parseInt(cleanedCpf.substring(i - 1, i)) * (11 - i)
    }
    remainder = (sum * 10) % 11
    if (remainder === 10 || remainder === 11) remainder = 0
    if (remainder !== parseInt(cleanedCpf.substring(9, 10)))
      return 'CPF inválido'
    sum = 0
    for (let i = 1; i <= 10; i++) {
      sum += parseInt(cleanedCpf.substring(i - 1, i)) * (12 - i)
    }
    remainder = (sum * 10) % 11
    if (remainder === 10 || remainder === 11) remainder = 0
    if (remainder !== parseInt(cleanedCpf.substring(10, 11)))
      return 'CPF inválido'
    return null
  }

  const validatePhone = (phone: string) => {
    const cleanedPhone = phone.replace(/\D/g, '')
    if (cleanedPhone.length < 10 || cleanedPhone.length > 11) {
      return 'Telefone inválido (DDD + número)'
    }
    return null
  }

  const validateCep = (cep: string) => {
    const cleanedCep = cep.replace(/\D/g, '')
    if (cleanedCep.length !== 8) {
      return 'CEP inválido (deve ter 8 dígitos)'
    }
    return null
  }

  const confirmedEmailMessage = () => {
    if (formData.confirmedEmail !== formData.email) {
      return 'Os e-mails não coincidem'
    }
    return null
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target

    if (name === 'phone') {
      const formattedPhone = formatPhone(value)
      setFormData(prev => ({ ...prev, [name]: formattedPhone }))
      setPhoneError(validatePhone(value))
    } else if (name === 'zipCode') {
      const formattedCep = formatCep(value)
      setFormData(prev => ({ ...prev, [name]: formattedCep }))
    } else if (name === 'document') {
      const formattedCpf = formatCpf(value)
      setFormData(prev => ({ ...prev, [name]: formattedCpf }))
      setCpfError(validateCpf(value))
    } else {
      setFormData(prev => ({ ...prev, [name]: value }))
    }
  }

  const handleCepBlur = async () => {
    const cleanedCep = formData.zipCode.replace(/\D/g, '')
    const cepValidation = validateCep(formData.zipCode)

    if (cepValidation) {
      setCepError(cepValidation)
      return
    } else {
      setCepError(null)
    }

    if (cleanedCep.length === 8 && cepError === null) {
      setIsFetchingCep(true)
      try {
        const response = await fetch(
          `https://viacep.com.br/ws/${cleanedCep}/json/`,
        )
        const data = await response.json()

        if (!data.erro) {
          setFormData(prev => ({
            ...prev,
            street: data.logradouro || '',
            neighborhood: data.bairro || '',
            city: data.localidade || '',
            state: data.uf || '',
          }))
          setCepError(null)
          toast({
            title: 'Endereço encontrado!',
            description: 'Os campos foram preenchidos automaticamente.',
          })
        } else {
          setCepError('CEP não encontrado')
        }
      } catch (error) {
        setCepError('Erro ao buscar CEP')
      } finally {
        setIsFetchingCep(false)
      }
    }
  }

  const handleApplyCoupon = async () => {
    setDiscountValue(0)
    setNewTotal(null)
    setCouponError(null)

    if (!couponCode.trim() || !originalPrice) return

    try {
      const result = await validateCoupon(couponCode.trim(), originalPrice)
      const discount = Number(result.data.attributes.discount_value)
      const finalPrice = Number(result.data.attributes.new_value)
      const couponId = result.data.attributes.coupon_id as string

      setAppliedCouponId(couponId)
      setDiscountValue(discount)
      setNewTotal(finalPrice)

      toast({
        title: 'Cupom aplicado!',
        description: `Desconto de R$ ${format(discount)} aplicado com sucesso.`,
      })
    } catch {
      const msg = 'Cupom inválido ou não aplicável.'
      setCouponError(msg)
      setShowCouponToast(true)
      toast({
        title: 'Erro',
        description: msg,
        variant: 'destructive',
      })
    }
  }

  const getTotalPrice = () => {
    if (!checkoutData) return 0

    const productPrice =
      checkoutData.selectedPageOption?.price || checkoutData.price
    const curatoryPrice = checkoutData.selectedCuratory?.value || 0
    const addonsPrice =
      checkoutData.selectedAddons?.reduce(
        (sum, addon) => sum + addon.price,
        0,
      ) || 0

    const totalBeforeDiscount =
      Number(productPrice) + Number(curatoryPrice) + Number(addonsPrice)

    if (newTotal !== null) {
      return newTotal
    }

    return totalBeforeDiscount
  }

  const isFormValid = () => {
    const requiredFields = [
      'email',
      'confirmedEmail',
      'name',
      'document',
      'paymentMethod',
      'phone',
      'zipCode',
      'street',
      'neighborhood',
      'city',
      'state',
      'number',
    ]

    const isRequiredFieldsFilled = requiredFields.every(
      field => formData[field as keyof typeof formData]?.trim() !== '',
    )

    return (
      isRequiredFieldsFilled &&
      !confirmedEmailMessage() &&
      !phoneError &&
      !cepError &&
      !cpfError &&
      termsAccepted
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!termsAccepted) {
      setTermsError(true)
      return
    }

    if (!isFormValid()) {
      setFormError(true)
      toast({
        title: 'Formulário incompleto',
        description:
          'Por favor, preencha todos os campos obrigatórios corretamente.',
        variant: 'destructive',
      })
      return
    }

    if (!checkoutData) {
      toast({
        title: 'Erro',
        description: 'Nenhum produto selecionado.',
        variant: 'destructive',
      })
      return
    }

    setTermsError(false)
    setFormError(false)
    setIsLoading(true)
    setApiErrors([])

    try {
      const payload = {
        email: formData.email,
        name: formData.name,
        phone: formData.phone.replace(/\D/g, ''),
        document: formData.document.replace(/\D/g, ''),
        productId: checkoutData.productId,
        dynamicProduct: checkoutData.selectedSubProduct?.id || '',
        dynamicLine: checkoutData.selectedPageOption?.id || '',
        curatoryId: checkoutData.selectedCuratory?.id,
        paymentMethod: formData.paymentMethod,
        installment:
          formData.paymentMethod === 'credit_card' ? formData.installment : '1',
        zipCode: formData.zipCode.replace(/\D/g, ''),
        street: formData.street,
        number: formData.number,
        address_line_2: formData.complement || '',
        neighborhood: formData.neighborhood,
        city: formData.city,
        state: formData.state,
        price: String(getTotalPrice()),
        couponId: appliedCouponId || undefined,
        selectedAddons: checkoutData.selectedAddons || [],
      }

      const response = await preRegister(payload)

      const checkoutUrl = response.data?.data?.attributes?.checkout_url

      if (!checkoutUrl) {
        throw new Error('URL de checkout não encontrada')
      }

      toast({
        title: 'Pedido processado com sucesso!',
        description: 'Você será redirecionado para o pagamento.',
      })

      navigate('/pedido-realizado', {
        state: {
          checkoutUrl,
          name: formData.name,
        },
      })

      window.open(checkoutUrl, '_blank')
    } catch (error: any) {
      console.error('Erro ao processar pedido:', error)

      if (error.response?.status === 422) {
        setApiErrors(error.response?.data?.errors || [])
      } else {
        setApiErrors([
          {
            title: 'Erro no servidor',
            detail:
              'Ocorreu um erro ao processar sua solicitação. Por favor, tente novamente.',
          },
        ])
      }

      toast({
        title: 'Erro ao processar pedido',
        description:
          error.response?.data?.errors?.[0]?.detail || 'Tente novamente.',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  // Loading state
  if (isLoadingCart) {
    return (
      <div className="min-h-screen flex flex-col">
        <MetaTags
          title="Finalizar Compra - FotoLivro Store"
          description="Complete seu pedido de fotolivros personalizados com segurança."
          type="website"
        />
        <Header />
        <main className="flex-1 container mx-auto px-4 py-16 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <h1 className="text-xl font-bold mb-2">
            Carregando dados do carrinho...
          </h1>
          <p className="text-muted-foreground">
            Por favor, aguarde um momento.
          </p>
        </main>
        <Footer />
      </div>
    )
  }

  // Error state
  if (cartError) {
    return (
      <div className="min-h-screen flex flex-col">
        <MetaTags
          title="Erro - FotoLivro Store"
          description="Erro ao carregar dados do carrinho."
          type="website"
        />
        <Header />
        <main className="flex-1 container mx-auto px-4 py-16 text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold mb-4">Erro ao carregar carrinho</h1>
          <p className="text-gray-600 mb-6">{cartError}</p>
          <Button onClick={() => navigate('/')}>Voltar para Home</Button>
        </main>
        <Footer />
      </div>
    )
  }

  if (!checkoutData) {
    return (
      <div className="min-h-screen flex flex-col">
        <MetaTags
          title="Finalizar Compra - FotoLivro Store"
          description="Complete seu pedido de fotolivros personalizados com segurança."
          type="website"
        />
        <Header />
        <main className="flex-1 container mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold mb-4">
            Nenhum Produto Selecionado
          </h1>
          <p className="text-muted-foreground mb-8">
            Selecione um produto para continuar com a compra
          </p>
          <Button onClick={() => navigate('/')}>Voltar para Home</Button>
        </main>
        <Footer />
      </div>
    )
  }

  const {
    productName,
    productImage,
    orientation,
    pages,
    selectedSubProduct,
    selectedPageOption,
    selectedCuratory,
    selectedAddons = [],
  } = checkoutData

  return (
    <div className="min-h-screen flex flex-col">
      <MetaTags
        title="Finalizar Compra - FotoLivro Store"
        description="Complete seu pedido de fotolivros personalizados com segurança."
        type="website"
      />
      <Header />
      <Breadcrumb />
      <main className="flex-1 bg-muted/30 py-12">
        <div className="container mx-auto px-4 max-w-6xl">
          {showCouponToast && couponError && (
            <CouponToast
              message={couponError}
              onClose={() => setShowCouponToast(false)}
            />
          )}

          <form onSubmit={handleSubmit}>
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="mb-6"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar
            </Button>

            <div className="mb-8">
              <h1 className="text-3xl font-bold">Finalizar Compra</h1>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <CardTitle>Dados de Entrega</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="name">Nome Completo</Label>
                      <Input
                        id="name"
                        name="name"
                        placeholder="Seu nome completo"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="seu@email.com"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="confirmedEmail">Confirmar Email</Label>
                        <Input
                          id="confirmedEmail"
                          name="confirmedEmail"
                          type="email"
                          placeholder="seu@email.com"
                          value={formData.confirmedEmail}
                          onChange={handleInputChange}
                          required
                        />
                        {confirmedEmailMessage() && (
                          <p className="text-red-500 text-sm mt-1">
                            {confirmedEmailMessage()}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="document">CPF</Label>
                        <Input
                          id="document"
                          name="document"
                          placeholder="000.000.000-00"
                          value={formData.document}
                          onChange={handleInputChange}
                          maxLength={14}
                          required
                        />
                        {cpfError && (
                          <p className="text-red-500 text-sm mt-1">
                            {cpfError}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="phone">Telefone/WhatsApp</Label>
                        <Input
                          id="phone"
                          name="phone"
                          placeholder="(11) 98765-4321"
                          value={formData.phone}
                          onChange={handleInputChange}
                          maxLength={15}
                          required
                        />
                        {phoneError && (
                          <p className="text-red-500 text-sm mt-1">
                            {phoneError}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="md:col-span-2">
                        <Label htmlFor="zipCode">CEP</Label>
                        <div className="relative">
                          <Input
                            id="zipCode"
                            name="zipCode"
                            placeholder="12345-678"
                            value={formData.zipCode}
                            onChange={handleInputChange}
                            onBlur={handleCepBlur}
                            maxLength={9}
                            required
                          />
                          {isFetchingCep && (
                            <Loader2 className="absolute right-3 top-3 h-5 w-5 animate-spin text-primary" />
                          )}
                        </div>
                        {cepError && (
                          <p className="text-red-500 text-sm mt-1">
                            {cepError}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="state">Estado</Label>
                        <Input
                          id="state"
                          name="state"
                          placeholder="SP"
                          value={formData.state}
                          onChange={handleInputChange}
                          maxLength={2}
                          required
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-4 gap-4">
                      <div className="md:col-span-3">
                        <Label htmlFor="street">Endereço</Label>
                        <Input
                          id="street"
                          name="street"
                          placeholder="Rua, Avenida..."
                          value={formData.street}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="number">Número</Label>
                        <Input
                          id="number"
                          name="number"
                          placeholder="123"
                          value={formData.number}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="complement">Complemento (opcional)</Label>
                      <Input
                        id="complement"
                        name="complement"
                        placeholder="Apto, Bloco..."
                        value={formData.complement}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="neighborhood">Bairro</Label>
                        <Input
                          id="neighborhood"
                          name="neighborhood"
                          placeholder="Bairro"
                          value={formData.neighborhood}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="city">Cidade</Label>
                        <Input
                          id="city"
                          name="city"
                          placeholder="Cidade"
                          value={formData.city}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="mt-6">
                  <CardHeader>
                    <CardTitle>Método de Pagamento</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Seção de Cupom */}
                    <div className="coupon-wrapper mb-6">
                      <div className="flex items-center gap-3">
                        <Input
                          ref={couponInputRef}
                          type="text"
                          value={couponCode}
                          disabled={!!searchParams.get('code')}
                          onChange={e => {
                            setCouponCode(e.target.value)
                            if (couponError) setCouponError(null)
                          }}
                          placeholder="Digite o código do cupom"
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          disabled={!!searchParams.get('code')}
                          onClick={handleApplyCoupon}
                        >
                          Aplicar cupom
                        </Button>
                      </div>
                      {appliedCouponId && couponCode !== '' && (
                        <div className="mt-3 p-3 bg-green-50 border border-green-200 text-green-700 rounded text-sm">
                          Cupom aplicado com sucesso! Desconto de R${' '}
                          {format(discountValue)}
                        </div>
                      )}
                    </div>

                    <div className="space-y-3">
                      <Label>Forma de Pagamento</Label>
                      <RadioGroup
                        value={formData.paymentMethod}
                        onValueChange={(value: 'pix' | 'credit_card') => {
                          setFormData(prev => ({
                            ...prev,
                            paymentMethod: value,
                          }))
                        }}
                      >
                        <div className="flex items-center space-x-3 border rounded-lg p-4 cursor-pointer hover:border-primary">
                          <RadioGroupItem value="pix" id="pix" />
                          <Label
                            htmlFor="pix"
                            className="cursor-pointer flex-1"
                          >
                            <div className="font-semibold">PIX</div>
                            <div className="text-sm text-muted-foreground">
                              Pagamento instantâneo
                            </div>
                          </Label>
                        </div>

                        <div className="flex items-center space-x-3 border rounded-lg p-4 cursor-pointer hover:border-primary">
                          <RadioGroupItem
                            value="credit_card"
                            id="credit_card"
                          />
                          <Label
                            htmlFor="credit_card"
                            className="cursor-pointer flex-1"
                          >
                            <div className="font-semibold">
                              Cartão de Crédito
                            </div>
                            <div className="text-sm text-muted-foreground">
                              Parcelamento em até 12x
                            </div>
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>

                    {formData.paymentMethod === 'credit_card' && (
                      <div>
                        <Label htmlFor="installment">Número de Parcelas</Label>
                        <select
                          id="installment"
                          name="installment"
                          value={formData.installment}
                          onChange={handleInputChange}
                          className="w-full h-12 bg-white dark:bg-dark-secondary border border-[#E3E5E6] rounded-md px-4 outline-none"
                        >
                          {Array.from({ length: 12 }, (_, i) => i + 1).map(
                            num => (
                              <option key={num} value={num}>
                                {num}x de R${' '}
                                {(getTotalPrice() / num)
                                  .toFixed(2)
                                  .replace('.', ',')}
                              </option>
                            ),
                          )}
                        </select>
                      </div>
                    )}

                    <div className="flex items-start space-x-3 space-y-0">
                      <input
                        type="checkbox"
                        id="terms"
                        checked={termsAccepted}
                        onChange={e => setTermsAccepted(e.target.checked)}
                        className="mt-1"
                      />
                      <div className="space-y-1 leading-none">
                        <Label htmlFor="terms">
                          Concordo com os{' '}
                          <a
                            href="/termos-e-condicoes"
                            target="_blank"
                            className="text-primary underline"
                          >
                            termos e condições
                          </a>
                        </Label>
                        {termsError && (
                          <p className="text-red-500 text-sm">
                            Você deve aceitar os termos e condições
                          </p>
                        )}
                        {formError && (
                          <p className="text-red-500 text-sm">
                            Há erros no formulário. Por favor, corrija-os antes
                            de prosseguir.
                          </p>
                        )}
                      </div>
                    </div>

                    {apiErrors.length > 0 && (
                      <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                        <ul className="list-disc pl-5 mt-2">
                          {apiErrors.map((error, index) => (
                            <li key={index}>
                              <strong>{error.title}:</strong> {error.detail}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              <div className="lg:col-span-1">
                <Card className="sticky top-4">
                  <CardHeader>
                    <CardTitle>Resumo do Pedido</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex gap-3 pb-3 border-b">
                      <img
                        src={productImage}
                        alt={productName}
                        className="w-16 h-16 object-cover rounded"
                      />
                      <div className="flex-1">
                        <h4 className="font-semibold text-sm">{productName}</h4>
                        <p className="text-xs text-muted-foreground">
                          {orientation} • {pages}p
                        </p>
                        {selectedSubProduct && (
                          <p className="text-xs text-muted-foreground">
                            {selectedSubProduct.horizontal}x
                            {selectedSubProduct.vertical} cm
                          </p>
                        )}
                        {selectedCuratory && (
                          <p className="text-xs text-muted-foreground">
                            Curadoria: {selectedCuratory.number_photos} fotos
                          </p>
                        )}
                        {selectedAddons && selectedAddons.length > 0 && (
                          <p className="text-xs text-muted-foreground">
                            +{selectedAddons.length} extra(s)
                          </p>
                        )}
                        <p className="text-sm font-semibold text-primary mt-2">
                          R${' '}
                          {selectedPageOption?.price?.toLocaleString('pt-BR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }) ||
                            checkoutData.price.toLocaleString('pt-BR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                        </p>
                      </div>
                    </div>

                    {/* Detalhes de adicionais */}
                    {selectedCuratory && (
                      <div className="border-t pt-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">
                            Curadoria de Fotos
                            <p className="text-xs">
                              {selectedCuratory.number_photos} fotos
                              selecionadas
                            </p>
                          </span>
                          <span className="font-medium">
                            R$ {format(selectedCuratory.value)}
                          </span>
                        </div>
                      </div>
                    )}

                    {selectedAddons && selectedAddons.length > 0 && (
                      <div className="border-t pt-3">
                        <p className="text-sm font-medium mb-2">Extras:</p>
                        {selectedAddons.map(addon => {
                          const discount = addon.original_price - addon.price
                          const discountPercentage = Math.round(
                            (discount / addon.original_price) * 100,
                          )
                          return (
                            <div
                              key={addon.id}
                              className="flex justify-between items-center text-sm mb-2 last:mb-0"
                            >
                              <div className="flex items-center gap-2">
                                <img
                                  src={addon.thumbnail_url || productImage}
                                  alt={addon.name}
                                  className="w-8 h-8 rounded"
                                />
                                <div>
                                  <p className="font-medium">{addon.name}</p>
                                  {discount > 0 && (
                                    <span className="text-xs text-green-600">
                                      -{discountPercentage}%
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                {discount > 0 && (
                                  <p className="text-xs text-gray-500 line-through">
                                    R${' '}
                                    {addon.original_price.toLocaleString(
                                      'pt-BR',
                                      {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      },
                                    )}
                                  </p>
                                )}
                                <p className="font-medium text-[#971BC9]">
                                  R${' '}
                                  {addon.price.toLocaleString('pt-BR', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </p>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}

                    {discountValue > 0 && (
                      <div className="border-t pt-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">
                            Desconto:
                          </span>
                          <span className="font-medium text-green-600">
                            - R$ {format(discountValue)}
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="pt-4 border-t space-y-2">
                      <div className="flex justify-between text-lg font-bold">
                        <span>Total:</span>
                        <span className="text-primary">
                          R${' '}
                          {getTotalPrice().toLocaleString('pt-BR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm font-semibold text-green-600">
                        <span>Frete:</span>
                        <span>GRÁTIS</span>
                      </div>
                    </div>

                    <div className="pt-4 border-t">
                      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Shield className="h-5 w-5 text-primary" />
                          <h4 className="font-semibold text-sm">
                            Compra 100% Segura
                          </h4>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Lock className="h-4 w-4" />
                            <span>Seus dados estão protegidos</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <CreditCard className="h-4 w-4" />
                            <span>Pagamento criptografado</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full"
                      size="lg"
                      disabled={isLoading || isFetchingCep}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Processando...
                        </>
                      ) : (
                        'Finalizar Pedido'
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default Checkout
